import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiConfig } from '../config/api';

const UserAuthContext = createContext();

export const useUserAuth = () => {
  const ctx = useContext(UserAuthContext);
  if (!ctx) throw new Error('useUserAuth must be used within UserAuthProvider');
  return ctx;
};

export const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        // Check rememberMe flag to determine which storage to prioritize
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        
        // Prioritize storage based on rememberMe preference
        let token, userData;
        if (rememberMe) {
          // Check localStorage first for persistent sessions
          token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
          userData = localStorage.getItem('user') || sessionStorage.getItem('user');
        } else {
          // Check sessionStorage first for temporary sessions
          token = sessionStorage.getItem('userToken') || localStorage.getItem('userToken');
          userData = sessionStorage.getItem('user') || localStorage.getItem('user');
        }
        
        if (token && userData) {
          // Check if token is expired
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Math.floor(Date.now() / 1000);
            if (payload.exp < now) {
              console.log('Token expired, clearing auth data');
              localStorage.removeItem('userToken');
              localStorage.removeItem('user');
              localStorage.removeItem('rememberMe');
              sessionStorage.removeItem('userToken');
              sessionStorage.removeItem('user');
            } else {
              const user = JSON.parse(userData);
              setUser(user);
              
              // Ensure data is in the correct storage based on rememberMe
              if (rememberMe) {
                // Ensure data is in localStorage for persistent sessions
                if (!localStorage.getItem('userToken')) {
                  localStorage.setItem('userToken', token);
                  localStorage.setItem('user', userData);
                }
                // Clear sessionStorage to avoid conflicts
                sessionStorage.removeItem('userToken');
                sessionStorage.removeItem('user');
              } else {
                // Ensure data is in sessionStorage for temporary sessions
                if (!sessionStorage.getItem('userToken')) {
                  sessionStorage.setItem('userToken', token);
                  sessionStorage.setItem('user', userData);
                }
                // Clear localStorage to avoid conflicts
                localStorage.removeItem('userToken');
                localStorage.removeItem('user');
                localStorage.removeItem('rememberMe');
              }
            }
          } catch (e) {
            console.log('Invalid token format, clearing auth data');
            localStorage.removeItem('userToken');
            localStorage.removeItem('user');
            localStorage.removeItem('rememberMe');
            sessionStorage.removeItem('userToken');
            sessionStorage.removeItem('user');
          }
        }
      } catch (error) {
        console.error('Error restoring session:', error);
        localStorage.removeItem('userToken');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('userToken');
        sessionStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };
    
    restoreSession();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const API_BASE_URL = apiConfig.baseURL;
      console.log('Attempting login to:', `${API_BASE_URL}/api/auth/login`);
      
      // Check cookie consent status
      const cookieConsent = localStorage.getItem('cookieConsent');
      const acceptsCookies = cookieConsent === 'accepted';
      
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Cookie-Consent': acceptsCookies ? 'accepted' : 'declined'
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ email, password })
      });
      
      console.log('Login response status:', res.status);
      
      if (!res.ok) {
        let errorMessage = 'Invalid email or password. Please try again.';
        try {
          const errorText = await res.text();
          console.error('Login failed:', res.status, errorText);
          // Try to parse as JSON to get the message
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch (parseError) {
            // If not JSON, use a generic message
            console.error('Error parsing error response:', parseError);
          }
        } catch (textError) {
          console.error('Error reading error response:', textError);
        }
        return { success: false, error: errorMessage };
      }
      
      const data = await res.json();
      console.log('Login response data:', data);
      
      if (!data.success) {
        // Handle password reset requirement
        if (data.requiresPasswordReset) {
          return { success: false, error: data.message, requiresPasswordReset: true, email: data.email || email };
        }
        // Handle OTP requirement
        if (data.requiresOTP) {
          return { success: false, error: data.message, requiresOTP: true, email: data.email || email };
        }
        // Handle email verification requirement
        if (data.requiresVerification) {
          return { success: false, error: data.message, requiresVerification: true, email: data.email || email };
        }
        return { success: false, error: data.message || 'Invalid email or password. Please try again.' };
      }
      const { user: userObj, token } = data.data;
      
      // Store token and user data based on rememberMe preference
      if (rememberMe) {
        // Persistent storage in localStorage
        localStorage.setItem('userToken', token);
        localStorage.setItem('user', JSON.stringify(userObj));
        localStorage.setItem('rememberMe', 'true');
        // Clear sessionStorage to avoid conflicts
        sessionStorage.removeItem('userToken');
        sessionStorage.removeItem('user');
      } else {
        // Temporary storage in sessionStorage only
        sessionStorage.setItem('userToken', token);
        sessionStorage.setItem('user', JSON.stringify(userObj));
        localStorage.removeItem('rememberMe');
        // Clear localStorage to avoid conflicts
        localStorage.removeItem('userToken');
        localStorage.removeItem('user');
      }
      
      setUser(userObj);
      return {
        success: true,
        user: userObj,
        requiresPasswordReset: Boolean(data.requiresPasswordReset || userObj.mustResetPassword),
        mustResetPassword: Boolean(data.mustResetPassword || userObj.mustResetPassword)
      };
    } catch (error) {
      console.error('Login network error:', error);
      return { success: false, error: 'Unable to connect to the server. Please check your internet connection and try again.' };
    }
  };

  const verifyOTP = async (email, otpCode) => {
    try {
      // Check cookie consent status
      const cookieConsent = localStorage.getItem('cookieConsent');
      const acceptsCookies = cookieConsent === 'accepted';
      
      const response = await fetch(`${apiConfig.baseURL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Cookie-Consent': acceptsCookies ? 'accepted' : 'declined'
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ email, otpCode })
      });

      const data = await response.json();

      if (data.success) {
        const { user: userObj, token } = data.data;
        
        // Store token and user data
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        if (rememberMe) {
          localStorage.setItem('userToken', token);
          localStorage.setItem('user', JSON.stringify(userObj));
        } else {
          sessionStorage.setItem('userToken', token);
          sessionStorage.setItem('user', JSON.stringify(userObj));
        }
        
        setUser(userObj);
        return { success: true, user: userObj };
      } else {
        return { success: false, error: data.message || 'Invalid OTP code. Please try again.' };
      }
    } catch (error) {
      console.error('OTP verification network error:', error);
      return { success: false, error: 'Unable to connect to the server. Please check your internet connection and try again.' };
    }
  };

  const resendOTP = async (email) => {
    try {
      const response = await fetch(`${apiConfig.baseURL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      return { success: data.success, message: data.message };
    } catch (error) {
      console.error('Resend OTP error:', error);
      return { success: false, message: 'Failed to resend OTP. Please try again.' };
    }
  };

  const setPermanentPassword = async (newPassword, confirmPassword) => {
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await fetch(`${apiConfig.baseURL}/api/auth/set-permanent-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword, confirmPassword })
      });
      const data = await res.json();
      if (data.success) {
        const storedStr = localStorage.getItem('user') || sessionStorage.getItem('user');
        if (storedStr) {
          try {
            const storedUser = JSON.parse(storedStr);
            storedUser.mustResetPassword = false;
            if (localStorage.getItem('user')) localStorage.setItem('user', JSON.stringify(storedUser));
            if (sessionStorage.getItem('user')) sessionStorage.setItem('user', JSON.stringify(storedUser));
            setUser(storedUser);
          } catch (e) {}
        }
      }
      return data;
    } catch (err) {
      return { success: false, message: err.message || 'Failed to set password' };
    }
  };

  const logout = async () => {
    // Delete session on backend
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      if (token) {
        await fetch(`${apiConfig.baseURL}/api/auth/logout`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear all possible token storage locations
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('user');
    // Also clear generic token keys and admin tokens that might be used
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    sessionStorage.removeItem('adminUser');
    setUser(null);
  };

  const isAuthenticated = () => !!user;
  const hasAnyRole = (roles) => {
    if (!user) return false;
    if (!roles || roles.length === 0) return true;
    return roles.includes(user.role);
  };

  const authHeader = () => ({ 
    authorization: `Bearer ${localStorage.getItem('userToken') || sessionStorage.getItem('userToken') || ''}` 
  });

  return (
    <UserAuthContext.Provider value={{ user, loading, login, logout, verifyOTP, resendOTP, setPermanentPassword, isAuthenticated, hasAnyRole, authHeader, setUser }}>
      {children}
    </UserAuthContext.Provider>
  );
};
